/**
 * 團隊頁與 workspace 入口的顯示決策，抽成純函式：沒有 React、沒有 `t()`、不讀時鐘。
 * 輸入是伺服器回的 summary／成員／context，輸出是「顯示哪個區塊、哪句 i18n key＋
 * 插值參數、哪個按鈕可按」。可不可以做由伺服器的 `capabilities` 決定，這裡不從
 * `fundingSource` 自己猜；金額只轉述伺服器給的數字，不算試用比例。
 *
 * 舊版 API 沒有 `capabilities`／`fundingSource` 時一律回付費呈現（可調整席次），
 * 與加欄位之前的畫面相同；伺服器端仍會各自拒絕不允許的操作。
 */

import type {
  OrganizationMemberSummary,
  SeatUsage,
  SwitchableContext,
  TeamCapabilityRefusal,
  TeamContinuation,
  TeamSubscriptionSummary,
} from '@kiao-voice/api-contract/organization'
import type { OrganizationMemberAccessAction } from '@kiao-voice/core'
import type { AppShellActive } from '../../lib/app-shell-active'
import { formatTwd, toRenewalDate } from './seat-adjustment'

/** 一句待翻譯的文案：i18n key 與插值參數。 */
export interface CopyRef {
  key: string
  params: Record<string, string | number>
}

export type TeamBillingMode =
  | { kind: 'paid'; canAdjust: boolean; adjustBlockedReason?: TeamCapabilityRefusal }
  | {
      kind: 'trial'
      giftSeats: number
      /** 贈送席位的到期日（ISO）；缺值時用試用結束日。 */
      giftEndsAt: string | null
      purchasedSeats: number
      canPurchase: boolean
      purchaseBlockedReason?: TeamCapabilityRefusal
      /** 下期是否收費；`null` ＝ 還沒買席位，試用結束後不會轉付費。 */
      continuation: TeamContinuation | null
      canCancel: boolean
      canResume: boolean
    }
  /** 試用已過期、等續訂排程交接成付費：試用操作全數關閉，只顯示轉換中。 */
  | { kind: 'trial_ended_pending' }

export function resolveTeamBillingMode(sub: TeamSubscriptionSummary): TeamBillingMode {
  const capabilities = sub.capabilities
  if (!capabilities || !sub.fundingSource) return { kind: 'paid', canAdjust: true }

  const purchase = capabilities.purchaseTrialSeats
  if (!purchase.allowed && purchase.reason === 'trial_ended') {
    return { kind: 'trial_ended_pending' }
  }

  if (sub.fundingSource === 'complimentary') {
    const continuation = sub.nextPeriod?.continuation ?? null
    return {
      kind: 'trial',
      giftSeats: sub.giftSeats ?? 0,
      giftEndsAt: sub.giftSeatsEndAt ?? sub.complimentaryEndsAt ?? null,
      purchasedSeats: sub.purchasedSeats ?? 0,
      canPurchase: purchase.allowed,
      ...(purchase.allowed ? {} : { purchaseBlockedReason: purchase.reason }),
      continuation,
      // 兩個操作在試用中都允許（冪等）；顯示哪一顆看目前的續訂狀態。
      canCancel: capabilities.cancelContinuation.allowed && continuation === 'consented',
      canResume:
        capabilities.resumeContinuation.allowed &&
        (continuation === 'canceled' || continuation === 'not_consented'),
    }
  }

  const adjust = capabilities.adjustPaidSeats
  return adjust.allowed
    ? { kind: 'paid', canAdjust: true }
    : { kind: 'paid', canAdjust: false, adjustBlockedReason: adjust.reason }
}

/** 試用結束後的續訂摘要；付費團隊（由席次區塊呈現）與沒買席位的試用回 `null`。 */
export function resolveNextPeriodLine(sub: TeamSubscriptionSummary): CopyRef | null {
  if (sub.fundingSource !== 'complimentary') return null
  const next = sub.nextPeriod
  if (!next) return null
  const date = toRenewalDate(next.startsAt) ?? next.startsAt
  switch (next.continuation) {
    case 'canceled':
      return { key: 'team.trialContinuationCanceled', params: { seats: next.seats, date } }
    case 'not_consented':
      return { key: 'team.trialContinuationNotConsented', params: { seats: next.seats, date } }
    case 'consented':
      return next.perSeatPriceTwd === null
        ? { key: 'team.trialContinuationLineNoPrice', params: { seats: next.seats, date } }
        : {
            key: 'team.trialContinuationLine',
            params: { seats: next.seats, price: formatTwd(next.perSeatPriceTwd), date },
          }
  }
}

/**
 * 轉正後的提醒：付費團隊還有停用中的成員時，請 owner 到成員區啟用要保留的人。
 * 只讀目前狀態，不觸發任何動作，所以重新整理不會重做停用。席數用已購數，不算贈送。
 */
export function resolveHandoffReminder(
  sub: TeamSubscriptionSummary,
  members: readonly OrganizationMemberSummary[],
  usage: SeatUsage
): CopyRef | null {
  if ((sub.fundingSource ?? 'paid') !== 'paid') return null
  const hasDisabled = members.some(
    (member) => member.status !== 'revoked' && member.accessState === 'disabled'
  )
  if (!hasDisabled) return null
  return {
    key: 'team.handoffReminderBody',
    params: { seats: sub.purchasedSeats ?? sub.seats ?? usage.total, used: usage.used },
  }
}

export type MemberAccessAction = OrganizationMemberAccessAction

/**
 * 成員列的啟用／停用按鈕。owner 不能被停用；邀請中的成員接受後才能啟用；
 * 缺 `accessState`（舊 API）視為使用中。
 */
export function resolveMemberAccessAction(
  member: OrganizationMemberSummary,
  viewerIsOwner: boolean
): MemberAccessAction | null {
  if (!viewerIsOwner || member.role === 'owner' || member.status !== 'active') return null
  return member.accessState === 'disabled' ? 'enable' : 'disable'
}

export interface MemberStatusView {
  key: string
  tone: 'default' | 'muted'
}

export function resolveMemberStatus(member: OrganizationMemberSummary): MemberStatusView {
  const disabled = member.accessState === 'disabled'
  if (member.status === 'invited') {
    return {
      key: disabled ? 'team.statusInviteNeedsEnable' : 'team.statusInvited',
      tone: 'muted',
    }
  }
  return disabled
    ? { key: 'team.statusDisabled', tone: 'muted' }
    : { key: 'team.statusActive', tone: 'default' }
}

export interface WorkspaceGateInput {
  /** 目前選取的 workspace；contexts 尚未載入時為 `null`。 */
  selected: SwitchableContext | null
  /** 目前的 app 頁面。 */
  active: AppShellActive
  /** 內容頁剛收到 `team_member_disabled`，而 contexts 還沒重新讀回來。 */
  denied: boolean
}

export type WorkspaceGate = 'content' | 'disabled_notice'

/**
 * 內容區要渲染頁面還是停用提示。停用的 workspace 仍可選取，但所有內容頁
 * 都換成提示、不掛載頁面；個人帳戶頁（`/app/account`）照常渲染。
 */
export function resolveWorkspaceGate({
  selected,
  active,
  denied,
}: WorkspaceGateInput): WorkspaceGate {
  if (active === 'account') return 'content'
  if (denied || selected?.accessState === 'disabled') return 'disabled_notice'
  return 'content'
}

/** A content refusal (`team_member_disabled`) for a workspace, and the contexts read before it. */
export interface AccessDenial {
  key: string
  contexts: readonly SwitchableContext[] | undefined
}

/**
 * Whether a content refusal still closes the selected workspace. It holds
 * until contexts read after it show that workspace usable again, so a failed
 * re-read keeps the notice instead of remounting content that will be refused.
 */
export function isAccessDenialCurrent(
  denial: AccessDenial | null,
  activeKey: string | undefined,
  contexts: readonly SwitchableContext[] | undefined
): boolean {
  if (!denial || denial.key !== activeKey) return false
  if (contexts === denial.contexts) return true
  const current = contexts?.find((context) => context.key === denial.key)
  return !current || current.accessState === 'disabled'
}
